/**
 * PACK BUILDER — 4 Sep 2026
 *
 * The pack is the cart. There is no second source of truth: every add is a real
 * /cart/add.js, every remove is a real /cart/change.js, and the counts you see
 * are read back from /cart.js rather than tallied from clicks. That is the whole
 * reason a pack survives moving between product pages — nothing has to be
 * carried across, because nothing was ever held locally.
 *
 * Three consequences worth knowing before editing this file:
 *
 *  1. The same variant can appear on more than one card (a sarong sits in its
 *     style collection AND in "shop all"). Painting must therefore address every
 *     card for a variant, never just the one that was clicked.
 *
 *  2. The pack total counts ONLY variants this builder has cards for. Counting
 *     the whole cart would let a hat push the shopper into a "3 pack" that the
 *     sarong discount will not honour at checkout — promising money off that
 *     never arrives is worse than showing no discount at all.
 *
 *  3. Adds are silent by default. Prestige opens its drawer on `product:added`
 *     and only re-renders on `cart:refresh`, so silent mode fires the latter.
 *     A drawer sliding over the builder after every tap makes building a pack
 *     of five genuinely difficult on a phone.
 */
(function () {
  'use strict';

  // Re-runnable on purpose. color-ajax-nav.js swaps the whole product section
  // when a colour swatch is tapped, and it deliberately does NOT re-run external
  // scripts — so the replacement markup arrives with no listeners on it. Without
  // this the builder is dead after the first colour change until a real reload,
  // which is exactly the sort of fault that looks like "the buttons stopped
  // working" rather than anything to do with swatches.
  function initAll() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-pack-builder]'),
      function (root) { init(root); }
    );
  }

  function init(root) {
    if (root.hasAttribute('data-pb-init')) return;   // never bind twice: two
    root.setAttribute('data-pb-init', '1');          // handlers = double charge

    var instantAdd = root.getAttribute('data-instant-add') !== 'false';
    var openDrawer = root.getAttribute('data-open-drawer') === 'true';
    var allowMulti = root.getAttribute('data-allow-multi') !== 'false';
    var tapCard    = root.getAttribute('data-tap-card') === 'true';
    var multiOpen  = root.getAttribute('data-multi-open') !== 'false';

    var statusEl = root.querySelector('[data-pack-status]');
    var ctaEl    = root.querySelector('[data-pack-cta]');

    var qtys = {};      // variantId -> quantity, mirrored from the cart
    var busy = {};      // variantId -> request in flight
    var readSeq = 0;    // only the newest cart read may write to the above
    var goal = 0;       // the pack size she tapped, so the tile stays lit
    var packBusy = false;   // a tier tap is in flight; card taps must wait

    var tiers = [];
    root.querySelectorAll('.PackBuilder__Tier').forEach(function (el) {
      var q = parseInt(el.getAttribute('data-qty') || '0', 10);
      if (q > 0) tiers.push({ qty: q, disc: parseInt(el.getAttribute('data-disc') || '0', 10), el: el });
    });
    tiers.sort(function (a, b) { return a.qty - b.qty; });

    function cardsFor(id) {
      return root.querySelectorAll('[data-card][data-variant-id="' + id + '"]');
    }
    function allCards() { return root.querySelectorAll('[data-card]'); }

    function total() {
      var t = 0;
      Object.keys(qtys).forEach(function (k) { t += qtys[k]; });
      return t;
    }

    // ── painting ────────────────────────────────────────────────────────────
    function paintVariant(id) {
      var q = qtys[id] || 0;
      cardsFor(id).forEach(function (card) {
        var badge = card.querySelector('[data-card-qty]');
        var minus = card.querySelector('[data-card-remove]');
        var add   = card.querySelector('[data-card-add]');
        // A card showing what is already IN the Set has no plus and no minus, so
        // a quantity badge on it reads as "you picked this" next to a control
        // that cannot change it. The variant still counts toward the pack total
        // — the shopper really is paying for it — it just must not be painted as
        // a choice she made here.
        if (card.classList.contains('is-in-set')) {
          if (badge) badge.hidden = true;
          if (minus) minus.hidden = true;
          card.classList.remove('is-selected');
          return;
        }
        card.classList.toggle('is-selected', q > 0);
        if (badge) { badge.textContent = q; badge.hidden = q === 0; }
        if (minus) minus.hidden = q === 0;
        if (add) {
          add.textContent = q > 0 ? (allowMulti ? 'Add' : 'Added') : 'Add';
          add.disabled = packBusy || !!busy[id] || (!allowMulti && q > 0);
        }
      });
    }

    function paintCats() {
      root.querySelectorAll('[data-cat]').forEach(function (cat) {
        var n = 0;
        cat.querySelectorAll('[data-card]').forEach(function (c) {
          n += qtys[c.getAttribute('data-variant-id')] || 0;
        });
        var out = cat.querySelector('[data-cat-count]');
        if (out) {
          out.textContent = n > 0 ? n + ' selected' : '0 selected';
          out.classList.toggle('is-active', n > 0);
        }
      });
    }

    // What the pack itself is worth right now, read from the cart rather than
    // multiplied out here. Only lines this builder has a card for are counted:
    // the cart may also hold a hat, and a hat must never appear in the pack's
    // price any more than it may push the shopper into a bigger tier.
    var packMoney = null;   // { count, total } in cents, or null until first read

    // One read, not two: hydrate() already fetches the cart and repaints, and it
    // now prices the pack from that same response.
    function refreshMoney() { return hydrate(); }

    function money(cents) {
      try { return window.formatShopMoney(cents); } catch (e) { return null; }
    }

    function paintTiers() {
      var t = total(), earned = null, next = null;
      tiers.forEach(function (tier) {
        if (t >= tier.qty) earned = tier;
        else if (!next) next = tier;
      });
      tiers.forEach(function (tier) {
        tier.el.classList.toggle('is-earned', earned === tier);
        // The size she tapped stays marked even before she has reached it, so
        // the tile reads as a target rather than as a button that did nothing.
        tier.el.classList.toggle('is-goal', goal > 0 && tier.qty === goal && earned !== tier);
      });
      if (statusEl) {
        // The price only appears when the cart agrees it is the pack's price —
        // packMoney.count matching what is on screen. If a request is still in
        // flight, or the two disagree, the line is written without a figure
        // rather than with a stale one.
        var m = (packMoney && packMoney.count === t) ? money(packMoney.total) : null;
        var head = t + ' in your pack' + (m ? ' · ' + m : '');
        if (t === 0) statusEl.textContent = '';
        else if (earned && !next) statusEl.textContent = head + ' · ' + earned.disc + '% off applied';
        else if (earned) statusEl.textContent = head + ' · ' + earned.disc + '% off · add ' + (next.qty - t) + ' more for ' + next.disc + '%';
        else if (next) statusEl.textContent = head + ' · add ' + (next.qty - t) + ' more for ' + next.disc + '% off';
        else statusEl.textContent = head;
      }
      if (ctaEl) {
        ctaEl.hidden = t === 0;
        var lbl = ctaEl.getAttribute('data-label-base');
        if (!lbl) { lbl = ctaEl.textContent.replace(/\s+/g, ' ').trim(); ctaEl.setAttribute('data-label-base', lbl); }
        var mm = (packMoney && packMoney.count === t) ? money(packMoney.total) : null;
        ctaEl.textContent = t > 0 ? lbl + ' · ' + t + (mm ? ' · ' + mm : '') : lbl;
      }
    }

    function paintAll() {
      allCards().forEach(function (c) { paintVariant(c.getAttribute('data-variant-id')); });
      paintCats();
      paintTiers();
    }

    // ── opening and closing a style ─────────────────────────────────────────
    // Every path that opens a style goes through here. When only one may be open
    // at a time, that rule has to hold whether the shopper tapped the header or
    // the builder opened it for her after filling a pack — applying it in one
    // place and forgetting it in the other is how two end up open at once.
    function closeCat(cat) {
      cat.classList.remove('is-open');
      var b = cat.querySelector('[data-cat-toggle]');
      if (b) b.setAttribute('aria-expanded', 'false');
    }

    function openCat(cat) {
      if (!multiOpen) {
        root.querySelectorAll('[data-cat].is-open').forEach(function (o) {
          if (o !== cat) closeCat(o);
        });
      }
      cat.classList.add('is-open');
      var b = cat.querySelector('[data-cat-toggle]');
      if (b) b.setAttribute('aria-expanded', 'true');
    }

    // ── cart traffic ────────────────────────────────────────────────────────
    function announce(qty) {
      if (openDrawer) {
        document.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true, detail: { quantity: qty, fromPackBuilder: true }
        }));
      } else {
        // Re-render the cart without the drawer sliding over the builder.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
      // Flagged so this builder's own listener does not re-read the cart it
      // just wrote — everything ELSE on the page still gets the event.
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { fromPackBuilder: true }
      }));
    }

    function setBusy(id, on) {
      busy[id] = on;
      cardsFor(id).forEach(function (card) {
        var a = card.querySelector('[data-card-add]');
        var m = card.querySelector('[data-card-remove]');
        if (a) a.disabled = on;
        if (m) m.disabled = on;
      });
    }

    function addOne(id) {
      // A tier tap computes absolute quantities from a snapshot of the cart and
      // POSTs them to /cart/update.js. A card add that lands in between is
      // therefore overwritten — the sarong appears, then quietly vanishes.
      if (packBusy) return;
      if (busy[id]) return;
      setBusy(id, true);
      var before = qtys[id] || 0;
      qtys[id] = before + 1;                 // optimistic: the tap feels instant
      paintVariant(id); paintCats(); paintTiers();

      fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: parseInt(id, 10), quantity: 1 })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function () { announce(1); })
        .then(refreshMoney)
        .catch(function () {
          qtys[id] = before;                 // Shopify refused — put it back
          paintVariant(id); paintCats(); paintTiers();
        })
        .finally(function () { setBusy(id, false); paintVariant(id); });
    }

    // /cart/change.js takes an ABSOLUTE quantity and a line number. A variant can
    // sit on more than one line when properties differ, so the line index is the
    // only unambiguous handle — matching on variant id alone would edit the wrong
    // line for anyone who also has that sarong in the cart from elsewhere.
    function removeOne(id) {
      if (packBusy) return;              // see addOne
      if (busy[id]) return;
      setBusy(id, true);
      var before = qtys[id] || 0;
      if (before <= 0) { setBusy(id, false); return; }
      qtys[id] = before - 1;
      paintVariant(id); paintCats(); paintTiers();

      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var line = 0, item = null;
          (cart.items || []).forEach(function (it, i) {
            if (!line && String(it.variant_id) === String(id)) { line = i + 1; item = it; }
          });
          if (!line) return null;
          return fetch('/cart/change.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: line, quantity: Math.max(0, item.quantity - 1) })
          });
        })
        .then(function (res) {
          // A refused removal was announced as a success, and because nothing
          // threw, the rollback in the catch never ran — so the badge went on
          // showing one fewer sarong than the cart actually held. addOne has
          // always checked this; removeOne never did.
          if (!res) return null;                       // the line was already gone
          if (!res.ok) throw new Error('change failed');
          announce(-1);
          return res;
        })
        .then(refreshMoney)
        .catch(function () {
          qtys[id] = before;
          paintVariant(id); paintCats(); paintTiers();
        })
        .finally(function () { setBusy(id, false); paintVariant(id); });
    }

    /**
     * Tapping a pack size: put ONE of the colour being viewed into the pack.
     *
     * It does not fill to the size, and it does not add a second one if that
     * colour is already in there — a second tap on a different tile is a change
     * of mind about the target, not a request for more of the same sarong. The
     * status line goes on doing the counting, so she can see how far off she is.
     */
    function startPackWithCurrent(target) {
      if (packBusy) return;
      goal = target > 0 ? target : 0;
      paintTiers();

      var id = root.getAttribute('data-current-variant');
      var cur = id ? cardsFor(id)[0] : null;
      // The colour on screen has to be one this builder carries AND buyable. A
      // sold-out colour still has a card, so tapping a size on its page would
      // otherwise send Shopify a variant it refuses.
      if (!cur || cur.classList.contains('is-gone') || cur.hasAttribute('data-in-set')) return;
      if ((qtys[id] || 0) > 0) return;        // already in the pack: nothing to do

      var cat = cur.closest('[data-cat]');
      if (cat && !cat.classList.contains('is-open')) openCat(cat);
      addOne(id);
    }

    // ── set the pack to a size ──────────────────────────────────────────────
    // Tapping a pack SETS the pack to that size, up or down. Tap 5 then tap 2 and
    // three come out. Anything else is a button that works in one direction only,
    // which is not something a shopper should have to discover.
    //
    // Removals come off the most recently added lines first — the closest thing
    // to an undo, and it keeps the colours she chose earliest, which are the ones
    // she thought about most.
    //
    // Both directions go through ONE request. /cart/update.js takes absolute
    // quantities keyed by variant, so a five-to-two step is a single write rather
    // than three separate removals that could half-fail and strand the pack in a
    // state matching neither the page nor the cart.

    function setPackBusy(on) {
      packBusy = on;
      root.querySelectorAll('.PackBuilder__Tier').forEach(function (t) {
        t.classList.toggle('is-busy', on);
      });
      // Card taps are refused while a tier tap is in flight, because that write
      // sends absolute quantities worked out before the tap existed. Refusing
      // silently would be its own fault — "I pressed Add and nothing happened"
      // is indistinguishable from a broken page — so the cards say so for the
      // length of the request.
      root.classList.toggle('is-pack-busy', on);
      root.querySelectorAll('[data-card-add], [data-card-remove]').forEach(function (btn) {
        if (on) { btn.setAttribute('data-pack-blocked', '1'); btn.disabled = true; }
        else { btn.removeAttribute('data-pack-blocked'); }
      });
      if (!on) paintAll();          // hands the buttons back to their own rules
    }

    function setPackTo(target) {
      if (packBusy) return;
      // No pre-flight equality test. total() is a mirror of the last cart we
      // saw, and the drawer can change the cart without telling us — so when it
      // had drifted low the tap did nothing at all and the tile looked dead. The
      // fresh read below already refuses the no-op, from data that cannot be
      // stale. The cost is one GET on a redundant tap, which is cheaper than a
      // button that ignores you.

      setPackBusy(true);

      // Declared out here because the step that reports what changed runs in a
      // LATER link of this chain. It used to be a var inside the callback below,
      // so `announce(target - have)` threw ReferenceError on every tier tap that
      // actually wrote to the cart. The catch at the end swallowed it, which is
      // why the pack still looked right: the write had already succeeded and the
      // catch repainted. What was lost was the announcement — and, worse, a real
      // failed write became indistinguishable from this one, because both landed
      // in the same catch.
      var have = 0;

      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var items = (cart.items || []).filter(function (it) {
            return cardsFor(String(it.variant_id)).length;   // sarongs only
          });

          // `have` comes from the SAME response as the quantities below, so the
          // two can never disagree.
          have = items.reduce(function (n, it) { return n + it.quantity; }, 0);
          if (target === have) return null;

          if (target > have) {
            var need = target - have;
            // The colour on screen has to be one this builder carries AND one
            // that can actually be bought. Checking only that a card exists was
            // not enough: a sold-out colour still has a card, so tapping a pack
            // on its page sent Shopify a variant it would refuse, and the whole
            // thing failed with nothing on screen to say why.
            var id = root.getAttribute('data-current-variant');
            var cur = id ? cardsFor(id)[0] : null;
            if (!cur || cur.classList.contains('is-gone') || cur.hasAttribute('data-in-set')) id = null;

            // Falling back used to take the first buyable card anywhere in the
            // builder, which on an OM page could hand the shopper three AURA
            // sarongs — a different style entirely — and could pick a card still
            // behind "Load more colours", so nothing visibly changed. Try the
            // style she is actually looking at first, and never offer a colour
            // that is only there to show what is already in the Set.
            var PICKABLE = '[data-card][data-variant-id]:not(.is-gone):not([data-in-set]):not([data-card-hidden])';
            if (!id && cur) {
              var ownCat = cur.closest('[data-cat]');
              var sameStyle = ownCat && ownCat.querySelector(PICKABLE);
              if (sameStyle) id = sameStyle.getAttribute('data-variant-id');
            }
            if (!id) {
              var first = root.querySelector(PICKABLE);
              if (first) id = first.getAttribute('data-variant-id');
            }
            if (!id) return null;
            return fetch('/cart/add.js', {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: parseInt(id, 10), quantity: need })
            }).then(function (r) {
              if (!r.ok) throw new Error('add failed');
              // Open the style holding it, so she can see what she just got.
              var card = cardsFor(id)[0];
              var cat = card && card.closest('[data-cat]');
              if (cat && !cat.classList.contains('is-open')) openCat(cat);
              return r;
            });
          }

          // Coming down: walk the cart backwards taking one off at a time until
          // the pack is the size asked for.
          var drop = have - target;
          var updates = {};
          for (var i = items.length - 1; i >= 0 && drop > 0; i--) {
            var take = Math.min(items[i].quantity, drop);
            updates[items[i].variant_id] = items[i].quantity - take;
            drop -= take;
          }
          if (!Object.keys(updates).length) return null;
          return fetch('/cart/update.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: updates })
          }).then(function (r) { if (!r.ok) throw new Error('update failed'); return r; });
        })
        .then(function (res) {
          if (res) announce(target - have);
          return hydrate();          // the cart is the truth; repaint from it
        })
        .catch(function (err) {
          // The cart is the truth either way, so repaint from it — but say so in
          // the console rather than failing silently, because this catch now
          // only ever sees a genuine problem.
          if (window.console && console.warn) console.warn('pack: could not set the pack size', err);
          hydrate();
        })
        .finally(function () { setPackBusy(false); });
    }

    // ── rehydrate: the bit that makes a pack survive moving pages ────────────
    function hydrate() {
      // Reads can land out of order — a tier tap and a card tap in quick
      // succession each trigger one, and the slower request can answer last with
      // the older cart. The token makes the newest read the only one allowed to
      // write, so the pack cannot silently revert to a state the shopper has
      // already moved past.
      var mine = ++readSeq;
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (mine !== readSeq) return;          // a newer read has already landed
          var next = {}, count = 0, cents = 0;
          (cart.items || []).forEach(function (it) {
            var id = String(it.variant_id);
            if (!cardsFor(id).length) return;   // not a sarong this builder offers
            next[id] = (next[id] || 0) + it.quantity;
            count += it.quantity;
            // final_line_price is after any automatic discount Shopify has
            // already applied, so this is what the shopper will actually pay.
            cents += (it.final_line_price != null ? it.final_line_price : it.line_price);
          });
          qtys = next;
          // Counted and priced from ONE response, so the two can never disagree
          // — the same rule that fixed the tier-tap bug this morning.
          packMoney = { count: count, total: cents };
          paintAll();
          document.dispatchEvent(new CustomEvent('pack:money', { detail: packMoney }));
        })
        .catch(function () {
          if (mine !== readSeq) return;
          // The cart could not be read, so we no longer know what the pack costs.
          // paintTiers() only prints a figure when packMoney agrees with the count
          // on screen, so clearing it falls back to a count with no price — which
          // is honest. Leaving the old figure showed a confident number for a cart
          // we had failed to read.
          packMoney = null;
          paintTiers();
        });
    }

    // ── scroll affordance on the pack row ───────────────────────────────────
    // The fade is only worth showing when something is actually hidden. A row
    // that already fits should not look clipped, and a row that does not fit
    // should not look like a rendering fault.
    var tierRow = root.querySelector('.PackBuilder__Tiers');
    function paintTierScroll() {
      if (!tierRow) return;
      tierRow.classList.toggle('is-scrollable', tierRow.scrollWidth > tierRow.clientWidth + 2);
    }
    paintTierScroll();
    // Every colour swap replaces the builder and runs init() again. Listeners
    // bound to window or document survive that, so after three swaps three dead
    // builders were still reacting to every resize and every cart event, each
    // painting a detached DOM. They now retire themselves the first time they
    // fire after their own root has left the page.
    function selfRetiring(target, type, fn, opts) {
      function wrapped(e) {
        if (!document.contains(root)) { target.removeEventListener(type, wrapped, opts); return; }
        fn(e);
      }
      target.addEventListener(type, wrapped, opts);
    }

    selfRetiring(window, 'resize', paintTierScroll, { passive: true });
    if (tierRow) tierRow.addEventListener('scroll', function () {
      // Drop the fade once the shopper has reached the end, so the last pack is
      // never dimmed while they are looking straight at it.
      var atEnd = tierRow.scrollLeft + tierRow.clientWidth >= tierRow.scrollWidth - 2;
      tierRow.classList.toggle('is-scrollable', !atEnd && tierRow.scrollWidth > tierRow.clientWidth + 2);
    }, { passive: true });

    // ── load more ───────────────────────────────────────────────────────────
    function paintMore(cat) {
      var btn = cat.querySelector('[data-cat-more]');
      if (!btn) return;
      var left = cat.querySelectorAll('[data-card][data-card-hidden]').length;
      if (!left) { btn.hidden = true; return; }
      btn.hidden = false;
      var out = btn.querySelector('[data-more-left]');
      if (out) out.textContent = '(' + left + ' more)';
    }
    root.querySelectorAll('[data-cat]').forEach(paintMore);

    function revealMore(cat, step) {
      var hidden = cat.querySelectorAll('[data-card][data-card-hidden]');
      for (var i = 0; i < step && i < hidden.length; i++) {
        hidden[i].removeAttribute('data-card-hidden');
        hidden[i].classList.remove('is-hidden');
      }
      paintMore(cat);
    }

    // ── image gallery ───────────────────────────────────────────────────────
    // Opens the colour's own photos. Deliberately a separate target from Add:
    // on a phone these cards are small, and a mis-tap that charges someone is a
    // far worse outcome than a mis-tap that opens a picture.
    var lb = null;
    function closeLb() {
      if (!lb) return;
      document.body.classList.remove('PackLb--open');
      lb.remove(); lb = null;
      document.removeEventListener('keydown', onLbKey);
    }
    function onLbKey(e) {
      if (e.key === 'Escape') closeLb();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    }
    var lbImages = [], lbIndex = 0;
    function paintLb() {
      if (!lb) return;
      var img = lb.querySelector('[data-lb-img]');
      var cnt = lb.querySelector('[data-lb-count]');
      if (img) img.src = lbImages[lbIndex];
      if (cnt) cnt.textContent = (lbIndex + 1) + ' / ' + lbImages.length;
      var prev = lb.querySelector('[data-lb-prev]');
      var next = lb.querySelector('[data-lb-next]');
      var many = lbImages.length > 1;
      if (prev) prev.hidden = !many;
      if (next) next.hidden = !many;
    }
    function step(d) {
      if (!lbImages.length) return;
      lbIndex = (lbIndex + d + lbImages.length) % lbImages.length;
      paintLb();
    }
    function openLb(handle, title) {
      fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return;
          lbImages = (p.images || []).map(function (u) {
            return u.replace(/(\.[a-z]{3,4})(\?|$)/i, '_1200x$1$2');
          });
          if (!lbImages.length) return;
          lbIndex = 0;
          lb = document.createElement('div');
          lb.className = 'PackLb';
          lb.innerHTML =
            '<div class="PackLb__Backdrop" data-lb-close></div>' +
            '<div class="PackLb__Body" role="dialog" aria-modal="true" aria-label="' +
              (title || 'Product photos').replace(/"/g, '&quot;') + '">' +
              '<button type="button" class="PackLb__Close" data-lb-close aria-label="Close">&times;</button>' +
              '<img class="PackLb__Img" data-lb-img alt="' + (title || '').replace(/"/g, '&quot;') + '">' +
              '<button type="button" class="PackLb__Nav PackLb__Nav--prev" data-lb-prev aria-label="Previous photo">&#8249;</button>' +
              '<button type="button" class="PackLb__Nav PackLb__Nav--next" data-lb-next aria-label="Next photo">&#8250;</button>' +
              '<p class="PackLb__Count" data-lb-count></p>' +
            '</div>';
          document.body.appendChild(lb);
          document.body.classList.add('PackLb--open');
          paintLb();
          document.addEventListener('keydown', onLbKey);

          lb.addEventListener('click', function (e) {
            if (e.target.closest('[data-lb-close]')) return closeLb();
            if (e.target.closest('[data-lb-prev]')) return step(-1);
            if (e.target.closest('[data-lb-next]')) return step(1);
          });

          // Swipe, because on a phone that is how people expect to move through
          // photos and the arrows are small.
          var x0 = null;
          lb.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
          lb.addEventListener('touchend', function (e) {
            if (x0 === null) return;
            var dx = e.changedTouches[0].clientX - x0;
            if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
            x0 = null;
          }, { passive: true });
        })
        .catch(function () { /* no gallery rather than a broken one */ });
    }

    // ── events ──────────────────────────────────────────────────────────────
    root.addEventListener('click', function (e) {
      var tierEl = e.target.closest('.PackBuilder__Tier');
      // On an upsell page the product on screen is not IN the pack, so "top the
      // pack up with the colour being viewed" has no colour to use — it would
      // reach for whichever card happened to be first and add three of a sarong
      // nobody chose. The tiers stay as a goal to aim at; only the cards add.
      var isUpsell = root.getAttribute('data-upsell') === '1';
      if (tierEl && !isUpsell && root.getAttribute('data-tier-fills') !== 'false') {
        e.preventDefault();
        // Tapping a pack size used to FILL the pack to that size with the colour
        // being viewed — tap "5 pack" on the Cacao page and you got five Cacao.
        // Nobody buys five of one colour; the whole point of the builder is
        // mixing. So a tap now starts the pack with ONE of the colour on screen
        // and leaves the rest to her, which is what the tile was always meant to
        // mean: a size to aim at, not an order to place.
        startPackWithCurrent(parseInt(tierEl.getAttribute('data-qty') || '0', 10));
        return;
      }
      var moreBtn = e.target.closest('[data-cat-more]');
      if (moreBtn) {
        e.preventDefault();
        revealMore(moreBtn.closest('[data-cat]'), parseInt(moreBtn.getAttribute('data-step') || '4', 10));
        return;
      }
      var zoom = e.target.closest('[data-card-zoom]');
      if (zoom) {
        e.preventDefault();
        var zc = zoom.closest('[data-card]');
        if (zc) openLb(zc.getAttribute('data-handle'),
                       (zc.querySelector('.PackBuilder__CardName') || {}).textContent);
        return;
      }
      var addBtn = e.target.closest('[data-card-add]');
      var remBtn = e.target.closest('[data-card-remove]');
      var head   = e.target.closest('[data-cat-toggle]');

      if (addBtn) {
        e.preventDefault();
        var c = addBtn.closest('[data-card]');
        if (c && instantAdd) addOne(c.getAttribute('data-variant-id'));
        return;
      }
      if (remBtn) {
        e.preventDefault();
        var c2 = remBtn.closest('[data-card]');
        if (c2) removeOne(c2.getAttribute('data-variant-id'));
        return;
      }
      if (head) {
        var cat = head.closest('[data-cat]');
        if (cat.classList.contains('is-open')) closeCat(cat);
        else openCat(cat);
        return;
      }
      if (tapCard) {
        var card = e.target.closest('[data-card]');
        if (card && !card.classList.contains('is-gone') && instantAdd) {
          addOne(card.getAttribute('data-variant-id'));
        }
      }
    });

    // Anything else that touches the cart — the single Add to Cart above, the
    // drawer's own steppers — should move these counts too.
    selfRetiring(document, 'cart:updated', function (e) {
      if (e && e.detail && e.detail.fromPackBuilder) return;
      hydrate();
    });
    // Only re-read on an add this builder did NOT make; ours already painted.
    selfRetiring(document, 'product:added', function (e) {
      if (e && e.detail && e.detail.fromPackBuilder) return;
      hydrate();
    });

    hydrate();
  }

  initAll();
  // Fired by color-ajax-nav.js after it swaps the product section.
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('page:loaded', initAll);
})();
